import React, { useState, useEffect } from 'react';
import { User, Edit3, Save, Settings, Shield, BarChart3, Trophy, Bell } from 'lucide-react';
import Header from '../../components/ui/Header';
import AvatarBuilder from './components/AvatarBuilder';
import UsernameManager from './components/UsernameManager';
import AccountSettings from './components/AccountSettings';
import ProfileStats from './components/ProfileStats';
import PrivacyControls from './components/PrivacyControls';
import ProfilePreview from './components/ProfilePreview';
import AdvancedSettings from './components/AdvancedSettings';
import TalentsAndAwards from './components/TalentsAndAwards';
import NotificationSettings from './components/NotificationSettings';
import { avatarService } from '../../services/avatarService';
import { userProfileService } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { UserProfileSkeleton, FadeSlideIn } from '../../components/ui/SkeletonLoader';

const UserProfile = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(location?.state?.activeTab || 'profile');
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '', 
    email: '',
    username: '',
    timezone: 'America/New_York',
    avatar: {
      config: {},
      seed: '',
      previewUrl: '',
      gender: 'male'
    },
    stats: {
      versesMemorized: 0,
      studyStreak: 0,
      communityContributions: 0
    },
    privacy: {
      reflectionVisibility: 'private',
      communityInteractions: true,
      showStats: true
    },
    notifications: {
      dailyReminders: true,
      planEmails: true,
      bibleReadingReminders: true,
      egwReadingReminders: false,
      studyUpdates: false,
      communityActivity: true
    },
    reminderSettings: {
      reminderTime: '08:00',
      reminderFrequency: 'daily',
      includeVerseInReminder: true,
      includeEgwInReminder: false,
      preferredBibleVersion: 'KJV',
      egwContentPreference: 'devotional'
    },
    memorization: {
      phase1Days: 30,
      dailyReviewDays: 60,
      weeklyReviewWeeks: 6,
      monthlyReviewMonths: 3,
      addToMemorizationInterval: 'weekly',
      addToMemorizationIntervalValue: 1
    }
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState('');

  // Load user profile data from database on mount
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user?.id) return;

      try {
        setIsLoading(true);
        const profile = await userProfileService?.getUserProfile(user?.id);

        // Fetch accurate stats: verse count from verses table, streak from user_streaks
        const [versesRes, streakRes, reflectionsRes] = await Promise.all([
          supabase?.from('verses')?.select('id', { count: 'exact', head: true })?.eq('user_id', user?.id),
          supabase?.from('user_streaks')?.select('current_streak')?.eq('user_id', user?.id)?.maybeSingle(),
          supabase?.from('verse_reflections')?.select('id', { count: 'exact', head: true })?.eq('user_id', user?.id)
        ]);

        const versesMemorized = versesRes?.count ?? profile?.stats?.versesMemorized ?? 0;
        const studyStreak = streakRes?.data?.current_streak ?? profile?.stats?.studyStreak ?? 0;
        const communityContributions = reflectionsRes?.count ?? 0;

        // Extract privacy settings stored inside notification_preferences JSONB
        const notifPrefs = profile?.notificationPreferences || {};
        const privacyFromDB = {
          reflectionVisibility: profile?.reflectionVisibility || 'private',
          communityInteractions: notifPrefs?.communityInteractions !== undefined ? notifPrefs?.communityInteractions : true,
          showStats: notifPrefs?.showStats !== undefined ? notifPrefs?.showStats : true
        };

        // Extract reminder settings stored inside notification_preferences JSONB
        const reminderFromDB = notifPrefs?.reminderSettings || {
          reminderTime: profile?.reminderSchedule || '08:00',
          reminderFrequency: 'daily',
          includeVerseInReminder: true,
          includeEgwInReminder: false,
          preferredBibleVersion: 'KJV',
          egwContentPreference: 'devotional'
        };

        // Core notification flags (exclude privacy/reminder sub-objects)
        const notificationsFromDB = {
          dailyReminders: notifPrefs?.dailyReminders !== undefined ? notifPrefs?.dailyReminders : true,
          planEmails: notifPrefs?.planEmails !== undefined ? notifPrefs?.planEmails : true,
          bibleReadingReminders: notifPrefs?.bibleReadingReminders !== undefined ? notifPrefs?.bibleReadingReminders : true,
          egwReadingReminders: notifPrefs?.egwReadingReminders !== undefined ? notifPrefs?.egwReadingReminders : false,
          studyUpdates: notifPrefs?.studyUpdates !== undefined ? notifPrefs?.studyUpdates : false,
          communityActivity: notifPrefs?.communityActivity !== undefined ? notifPrefs?.communityActivity : true
        };
        
        setProfileData({
          firstName: profile?.firstName || '',
          lastName: profile?.lastName || '',
          email: profile?.email || '',
          username: profile?.username || '',
          timezone: profile?.timezone || 'America/New_York',
          avatar: profile?.avatar || {
            config: {},
            seed: '',
            previewUrl: '',
            gender: 'male'
          },
          stats: {
            versesMemorized,
            studyStreak,
            communityContributions
          },
          privacy: privacyFromDB,
          notifications: notificationsFromDB,
          reminderSettings: reminderFromDB,
          memorization: {
            phase1Days: profile?.memorization?.phase1Days || 30,
            dailyReviewDays: profile?.memorization?.dailyReviewDays || 60,
            weeklyReviewWeeks: profile?.memorization?.weeklyReviewWeeks || 6,
            monthlyReviewMonths: profile?.memorization?.monthlyReviewMonths || 3,
            addToMemorizationInterval: 'weekly',
            addToMemorizationIntervalValue: Math.floor((profile?.memorization?.addToMemorizationIntervalDays || 7) / 7)
          }
        });
      } catch (error) {
        console.error('Error loading user profile:', error);
        setSaveError('Failed to load profile data');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserProfile();
  }, [user?.id]);

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'account', label: 'Account', icon: Settings },
    { id: 'privacy', label: 'Privacy', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'advanced', label: 'Advanced', icon: BarChart3 },
    { id: 'awards', label: 'Talents & Awards', icon: Trophy }
  ];

  const handleSaveProfile = async () => {
    if (!user?.id) {
      setSaveError('User not authenticated');
      return;
    }

    try {
      setIsSaving(true);
      setSaveError('');

      // Save avatar if it was updated
      if (profileData?.avatar) {
        if (profileData?.avatar?.svgContent) {
          await avatarService?.updateUserAvatar(
            user?.id,
            profileData?.avatar?.config || {},
            profileData?.avatar?.seed,
            null,
            profileData?.avatar?.svgContent
          );
        } else {
          await avatarService?.getOrCreateAvatar(
            user?.id,
            profileData?.avatar?.config || {},
            profileData?.avatar?.gender
          );
        }
      }

      // Convert interval to days for database storage
      const intervalDays = profileData?.memorization?.addToMemorizationInterval === 'daily'
        ? profileData?.memorization?.addToMemorizationIntervalValue
        : profileData?.memorization?.addToMemorizationInterval === 'weekly'
        ? profileData?.memorization?.addToMemorizationIntervalValue * 7
        : profileData?.memorization?.addToMemorizationIntervalValue * 30;

      // Build the full notification_preferences JSONB that stores:
      // - core notification flags
      // - privacy toggles (communityInteractions, showStats)
      // - reminder settings object
      const fullNotificationPreferences = {
        ...profileData?.notifications,
        communityInteractions: profileData?.privacy?.communityInteractions,
        showStats: profileData?.privacy?.showStats,
        reminderSettings: profileData?.reminderSettings
      };

      // Save all profile data to database
      await userProfileService?.updateUserProfile(user?.id, {
        username: profileData?.username,
        firstName: profileData?.firstName,
        lastName: profileData?.lastName,
        email: profileData?.email,
        reflectionVisibility: profileData?.privacy?.reflectionVisibility,
        notificationPreferences: fullNotificationPreferences,
        reminderSchedule: profileData?.reminderSettings?.reminderTime || '08:00',
        memorization: {
          phase1Days: profileData?.memorization?.phase1Days,
          dailyReviewDays: profileData?.memorization?.dailyReviewDays,
          weeklyReviewWeeks: profileData?.memorization?.weeklyReviewWeeks,
          monthlyReviewMonths: profileData?.memorization?.monthlyReviewMonths,
          addToMemorizationIntervalDays: intervalDays
        }
      });
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveError(error?.message || 'Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateProfile = (updates) => {
    setProfileData(prev => ({ ...prev, ...updates }));
  };

  // Shared Save/Cancel button group
  const SaveCancelButtons = () => (
    <div className="flex space-x-3">
      <button
        onClick={() => setIsEditing(false)}
        disabled={isSaving}
        className="px-4 py-2 border border-border rounded-lg text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50 text-sm"
      >
        Cancel
      </button>
      <button
        onClick={handleSaveProfile}
        disabled={isSaving}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2 disabled:opacity-50 text-sm font-medium"
      >
        {isSaving ? (
          <>
            <div className="animate-spin h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
            <span>Saving...</span>
          </>
        ) : (
          <>
            <Save className="w-4 h-4" />
            <span>Save Profile</span>
          </>
        )}
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <UserProfileSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <Header />
      {/* Main Profile Content */}
      <div className="p-4">
        <FadeSlideIn className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="bg-card rounded-2xl border border-border p-6 border-l-4 border-l-primary shadow-warm-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-4">
                <div className="h-16 w-16 bg-gradient-to-br from-primary to-amber-600 rounded-full flex items-center justify-center shadow-warm-md">
                  <User className="w-8 h-8 text-primary-foreground" />
                </div>
                <div>
                  <h1 className="text-2xl font-heading font-bold text-foreground">My Profile</h1>
                  <p className="text-muted-foreground text-sm">Manage your account and personalize your Fortified Mind experience</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* Show Save Profile button at top when editing */}
                {isEditing && <SaveCancelButtons />}
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    data-edit-profile-button
                    className="flex items-center space-x-2 px-5 py-2.5 rounded-xl border-2 font-semibold text-sm transition-all shadow-md hover:shadow-lg bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <Edit3 className="w-4 h-4" /><span>Edit Profile</span>
                  </button>
                )}
              </div>
            </div>
            {/* Error message at top */}
            {saveError && isEditing && (
              <div className="mt-4 bg-destructive/10 border border-destructive/30 rounded-lg p-3">
                <p className="text-sm text-destructive">{saveError}</p>
              </div>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="bg-card rounded-2xl border border-border p-2 shadow-warm">
            <div className="flex flex-wrap gap-1">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab?.label}</span>
                </button>
              ))}
              {/* Security shortcut */}
              <button
                onClick={() => navigate('/security')}
                className="flex items-center space-x-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Shield className="w-4 h-4" />
                <span>Security</span>
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'profile' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Avatar & Username */}
                <div className="lg:col-span-2 space-y-6">
                  <AvatarBuilder
                    avatar={profileData?.avatar}
                    onUpdate={(avatar) => handleUpdateProfile({ avatar })}
                    isEditing={isEditing}
                  />
                  <UsernameManager
                    username={profileData?.username}
                    onUpdate={(username) => handleUpdateProfile({ username })}
                    isEditing={isEditing}
                  />
                </div>

                {/* Right Column - Preview & Stats */}
                <div className="space-y-6">
                  <ProfilePreview profileData={profileData} />
                  <ProfileStats stats={profileData?.stats} />
                </div>
              </div>
            )}

            {activeTab === 'account' && (
              <AccountSettings
                profileData={profileData}
                onUpdate={handleUpdateProfile}
                isEditing={isEditing}
              />
            )}

            {activeTab === 'privacy' && (
              <PrivacyControls
                privacy={profileData?.privacy}
                notifications={profileData?.notifications}
                onUpdate={handleUpdateProfile}
                isEditing={isEditing}
              />
            )}

            {activeTab === 'notifications' && (
              <NotificationSettings
                profileData={profileData}
                onUpdate={handleUpdateProfile}
                isEditing={isEditing}
              />
            )}

            {activeTab === 'advanced' && (
              <AdvancedSettings
                profileData={profileData}
                onUpdate={handleUpdateProfile}
                isEditing={isEditing}
              />
            )}

            {activeTab === 'awards' && (
              <TalentsAndAwards userId={user?.id} />
            )}
          </div>

          {/* Save Changes Button at Bottom */}
          {isEditing && (
            <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Save className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Save Changes</p>
                    <p className="text-sm text-muted-foreground">Your changes will be saved securely</p>
                  </div>
                </div>
                <SaveCancelButtons />
              </div>
            </div>
          )}

          {/* Bottom padding for mobile navigation */}
          <div className="md:hidden pb-20" />
        </FadeSlideIn>
      </div>
    </div>
  );
};

export default UserProfile;